import { useState } from 'react';
import Alert from '~/components/Alert';
import Button from '~/components/button/Button';
import Submit from '~/components/button/Submit';
import Form from '~/components/Form';
import Input from '~/components/form-controls/Input';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import Link from '~/components/Link';
import {
  FormKeys,
  useCrewOrderLookupActionResult,
  useCrewOrderLookupData,
  useOrderLookupActionResult,
} from './orderLookup';

function OrderLookupDetailText({ text }: { text: string | undefined }) {
  if (!text) return null;

  return (
    <div className="flex items-start gap-2 border-none text-on-surface">
      <Icon icon="InfoCircle" className="size-6 shrink-0" />
      {text}
    </div>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  return message ? <Alert variant="error" title={message} /> : null;
}

function RegistrationLink({ linkText }: { linkText?: string }) {
  return linkText ? <Link to="register">{linkText}</Link> : null;
}

function GiftReturnLookup({
  orderNumberDescription,
}: {
  orderNumberDescription?: string;
}) {
  return (
    <>
      <Input
        id={FormKeys.shippingPostalCode}
        name={FormKeys.shippingPostalCode}
        label="Shipping Postal Code"
        autoComplete="postal-code"
        required
        validationMessages={{
          valueMissing: 'Please enter the postal code of the shipping address.',
        }}
      />

      <Input
        id={FormKeys.orderOrTrackingNumber}
        name={FormKeys.orderOrTrackingNumber}
        label="Order Number or Tracking Number"
        description={`${orderNumberDescription ?? 'E.g. 123456, RS-98765, etc.'}`}
        required
        validationMessages={{
          valueMissing: 'Please enter your order number or tracking number.',
        }}
      />
      <input
        id={FormKeys.isGiftReturn}
        name={FormKeys.isGiftReturn}
        value="true"
        type="hidden"
      />
    </>
  );
}

function OrderLookupFields({
  orderLookupLabel,
  orderNumberDescription,
}: {
  orderLookupLabel: string;
  orderNumberDescription?: string;
}) {
  return (
    <>
      <Input
        id={FormKeys.emailOrPostalCode}
        name={FormKeys.emailOrPostalCode}
        label="Email Address or Postal Code"
        autoComplete="email postal-code"
        required
        validationMessages={{
          valueMissing: 'Please enter your email address or postal code.',
        }}
      />

      <Input
        id={FormKeys.orderNo}
        name={FormKeys.orderNo}
        label={orderLookupLabel}
        description={`${orderNumberDescription ?? 'E.g. 123456, RS-98765, etc.'}`}
        required
        validationMessages={{
          valueMissing: 'Please enter your order number.',
        }}
      />
    </>
  );
}

type OrderLookupPageProps = {
  orderLookupDetailText?: string;
  orderNumberDescription?: string;
  orderLookupLabel?: string;
  startRegistrationText?: string;
  isGiftReturnEnabled?: boolean;
  orderLookupGiftReturnDetailText?: string;
  errorMessage?: string;
};

function OrderLookup(props: OrderLookupPageProps) {
  const {
    orderLookupDetailText,
    orderNumberDescription,
    orderLookupLabel = 'Order Number',
    startRegistrationText,
    errorMessage,
    isGiftReturnEnabled,
    orderLookupGiftReturnDetailText,
  } = props;

  const [lookUpByGiftReturn, setLookUpByGiftReturn] = useState(false);

  const lookupText = lookUpByGiftReturn
    ? orderLookupGiftReturnDetailText
    : orderLookupDetailText;

  return (
    <Page.Root title="Order Look Up" hideHeader>
      <OrderLookupDetailText text={lookupText} />
      <Form className="w-full items-start">
        {lookUpByGiftReturn ? (
          <GiftReturnLookup orderNumberDescription={orderNumberDescription} />
        ) : (
          <OrderLookupFields
            orderLookupLabel={orderLookupLabel}
            orderNumberDescription={orderNumberDescription}
          />
        )}

        <ErrorMessage message={errorMessage} />

        <Submit>Look It Up</Submit>
        {isGiftReturnEnabled && (
          <Button
            className="w-full md:w-1/2"
            variant="outlined"
            onClick={() => setLookUpByGiftReturn(!lookUpByGiftReturn)}
          >
            {lookUpByGiftReturn
              ? 'Not a gift? Switch back'
              : 'Returning a gift?'}
          </Button>
        )}
      </Form>

      <RegistrationLink linkText={startRegistrationText} />
    </Page.Root>
  );
}

export function BasicOrderLookupPage() {
  const actionResult = useOrderLookupActionResult();
  const errorMessage =
    actionResult?.ok === false ? actionResult.message : undefined;

  return <OrderLookup errorMessage={errorMessage} />;
}

export function BrandedOrderLookupPage() {
  const actionResult = useCrewOrderLookupActionResult();
  const {
    orderNumberDescription,
    orderLookupDetailText,
    orderLookupLabel,
    startRegistrationText,
    isGiftReturnEnabled,
    orderLookupGiftReturnDetailText,
  } = useCrewOrderLookupData();
  const errorMessage =
    actionResult?.ok === false ? actionResult.message : undefined;

  return (
    <OrderLookup
      errorMessage={errorMessage}
      orderNumberDescription={orderNumberDescription}
      orderLookupDetailText={orderLookupDetailText}
      orderLookupLabel={orderLookupLabel}
      startRegistrationText={startRegistrationText}
      isGiftReturnEnabled={isGiftReturnEnabled}
      orderLookupGiftReturnDetailText={orderLookupGiftReturnDetailText}
    />
  );
}
